import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

export default function Memberships() {
  const [tiers, setTiers] = useState([]);
  const [selected, setSelected] = useState(null);
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/memberships").then(({data})=>setTiers(data)); }, []);
  useEffect(() => { if (user) setForm(f=>({...f, name: user.name, email: user.email, phone: user.phone||""})); }, [user]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/memberships/request", { tier: selected.id, ...form });
      toast.success("Membership request received", { description: "We'll reach out within one business day to finalize." });
      setSelected(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="memberships-page">
      <section className="section-y pb-12">
        <div className="container-x max-w-3xl">
          <div className="overline">Memberships</div>
          <h1 className="font-display text-5xl lg:text-6xl text-sage-800 mt-4 text-balance">Wellness works better when it's consistent.</h1>
          <p className="mt-6 text-lg text-sage-800/70">Pick a plan that fits your rhythm — cancel anytime, and unlock priority booking, member pricing, and ongoing care.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid gap-6 lg:grid-cols-3 items-stretch">
          {tiers.map((t) => (
            <div key={t.id} data-testid={`tier-${t.id}`} className={`relative rounded-3xl border p-8 flex flex-col ${t.featured ? "bg-sage-700 text-white border-sage-700 shadow-[0_20px_60px_rgba(148,114,47,0.22)] lg:-mt-6" : "bg-white border-sand-200/60"}`}>
              {t.featured && <div className="absolute -top-3 right-6 bg-clay-500 text-white text-xs px-3 py-1 rounded-full uppercase tracking-widest">Most popular</div>}
              <div className={`overline ${t.featured?"text-clay-400":""}`}>{t.name}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-display text-5xl">${t.price}</div>
                <div className={`text-sm ${t.featured?"text-sand-100/70":"text-sage-800/60"}`}>/ {t.cadence}</div>
              </div>
              <p className={`mt-4 text-sm ${t.featured?"text-sand-100/85":"text-sage-800/70"}`}>{t.description}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.perks.map((p, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-sm"><Check className={`w-4 h-4 mt-0.5 ${t.featured?"text-clay-400":"text-sage-600"}`} />{p}</li>
                ))}
              </ul>
              <Button data-testid={`tier-join-${t.id}`} onClick={()=>setSelected(t)} className={`btn-pill h-11 mt-8 ${t.featured ? "bg-white text-sage-800 hover:bg-sand-100" : "bg-sage-700 hover:bg-sage-800 text-white"}`}>Join {t.name}</Button>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o)=>!o && setSelected(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="join-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Join {selected?.name}</DialogTitle>
            <p className="text-sm text-sage-800/60">We'll confirm your membership by email within one business day.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="join-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="join-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} data-testid="join-phone" /></div>
            <div><Label>Anything we should know?</Label><Textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} data-testid="join-notes" /></div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={submitting || !form.name || !form.email} className="btn-pill bg-sage-700 hover:bg-sage-800 text-white w-full h-11" data-testid="join-submit">
              {submitting ? "Submitting…" : `Request ${selected?.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
